/**
 * src/components/shared/TaskNotesSection.jsx — Notes, materials, voice, photo for TaskDetailPanel
 * AC Nautitech Executive Assistant
 *
 * Notes editing, quick notes, voice notes, photo upload, and materials section.
 * Extracted from TaskDetailPanel.jsx to reduce file size.
 */

import { useState, useEffect } from 'react';
import { Camera, Edit3, MessageSquare, Package, MapPin, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../../lib/api/client';
import EntityMicButton from './EntityMicButton';
import DictateFieldButton from './DictateFieldButton';
import useVoiceBreadcrumbs from '../../hooks/useVoiceBreadcrumbs';
import BreadcrumbCard from './BreadcrumbCard';
import { fetchTaskMaterials, fetchMaterialRequests, createMaterialRequest } from '../../lib/api/procurement';
import { logAppError } from '../../lib/errorReporter';
import { useT } from '../../lib/i18n/I18nContext';
import { markPickerOpen, clearPickerOpen } from '../../lib/nav/reloadRestore';
import { MATERIAL_REQUEST_STATUSES } from '../../lib/constants/procurement';
import { isBlocked } from '../../lib/constants/taskStatus';

const inp = {
  width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--bdr)',
  background: 'var(--card)', fontSize: 13, color: 'var(--t1)', outline: 'none', minHeight: 44,
};

const glassBox = {
  background: 'var(--glass)', borderRadius: 10, padding: 12, border: '1px solid var(--bdr)',
};

const labelRow = {
  fontSize: 11, color: 'var(--tm)', marginBottom: 4,
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};

const editBtn = {
  background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: '2px 6px',
};

export default function TaskNotesSection({
  task,
  statusSlug,
  projectId,
  projName,
  saving,
  onUpdate,
}) {
  const { t } = useT();
  const f = task || {};

  // Notes editing
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(f.notes || '');

  // Quick note state (INST-06)
  const [quickNote, setQuickNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Photo upload state (INST-04)
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState(null);

  // Materials state (C6 + C5)
  const [taskMaterials, setTaskMaterials] = useState([]);
  const [materialRequests, setMaterialRequests] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({ material_name: '', qty: 1, urgency: 'normal', notes: '' });

  // Voice breadcrumbs for this task
  const { notes: voiceNotes, refresh: refreshVoice } = useVoiceBreadcrumbs({
    contextType: 'task', contextId: task?.id, limit: 3,
  });

  useEffect(() => {
    if (!task?.id) return;
    // TICKET-050 (AEGIS MED 2026-05-01) — pre-fix `.catch(() => {})` swallowed
    // material/request fetch failures silently. Now: surface to app_error_logs
    // so the materials section's empty state can be diagnosed when broken.
    fetchTaskMaterials(task.id).then(setTaskMaterials).catch((e) => {
      logAppError({
        type: 'api_error',
        message: `TaskNotesSection.fetchTaskMaterials failed: ${e?.message || e}`,
        stack: e?.stack,
        view: 'task.notes',
        metadata: { task_id: task.id, fn: 'fetchTaskMaterials' },
      });
    });
    fetchMaterialRequests({ task_id: task.id }).then(setMaterialRequests).catch((e) => {
      logAppError({
        type: 'api_error',
        message: `TaskNotesSection.fetchMaterialRequests failed: ${e?.message || e}`,
        stack: e?.stack,
        view: 'task.notes',
        metadata: { task_id: task.id, fn: 'fetchMaterialRequests' },
      });
    });
  }, [task?.id]);

  // Reset on task change (handled by parent key prop, but also defensive)
  useEffect(() => {
    setNotesDraft(f.notes || '');
    setEditingNotes(false);
    setQuickNote('');
    setPhotoError(null);
  }, [task?.id]);

  const doUpdate = async (updates) => {
    await onUpdate(updates);
  };

  return (
    <>
      {/* Notes */}
      <div style={glassBox}>
        <div style={{ ...labelRow, marginBottom: 6 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <MessageSquare size={12} /> Notes
          </span>
          <button onClick={() => setEditingNotes(v => !v)} style={editBtn}>
            {editingNotes ? 'Cancel' : <Edit3 size={12} />}
          </button>
        </div>
        {editingNotes ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)} rows={3}
                style={{ ...inp, resize: 'none', flex: 1 }} placeholder="Add notes..." />
              <DictateFieldButton
                kind="task_note"
                ariaLabel={t('task.dictateNotes', { defaultValue: 'Dictate notes' })}
                onInsert={(polished) => setNotesDraft((prev) => (prev?.trim() ? `${prev}\n${polished}` : polished))}
              />
            </div>
            <button onClick={() => { doUpdate({ notes: notesDraft }); setEditingNotes(false); }}
              disabled={saving} style={{
                padding: '10px 14px', borderRadius: 10, background: 'var(--navy-surface)', color: 'white',
                fontSize: 13, fontWeight: 600, alignSelf: 'flex-end', cursor: 'pointer', border: 'none',
              }}>
              Save Notes
            </button>
          </div>
        ) : (
          <div style={{
            fontSize: 13, color: notesDraft ? 'var(--t1)' : 'var(--tm)',
            fontStyle: notesDraft ? 'normal' : 'italic', lineHeight: 1.5,
            cursor: 'pointer', minHeight: 20,
          }} onClick={() => setEditingNotes(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (() => setEditingNotes(true))(e); } }}>
            {notesDraft || 'No notes \u2014 tap to add'}
          </div>
        )}
      </div>

      {/* Materials Section (C6 + C3 + C5) */}
      {(taskMaterials.length > 0 || materialRequests.length > 0 || isBlocked(statusSlug)) && (
        <div style={{ ...glassBox }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', marginBottom: 8,
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <Package size={14} /> Materials
          </div>
          {/* PO-linked materials */}
          {taskMaterials.map(m => {
            const isWaiting = MATERIAL_REQUEST_STATUSES.includes(m.status);
            return (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: m.status === 'received' ? 'var(--ok)' : isWaiting ? 'var(--warn)' : 'var(--bad)',
                }} />
                <span style={{ flex: 1, color: 'var(--t1)' }}>
                  {m.material?.name || m.description}
                  {m.material?.bin_location && (
                    <span style={{ color: 'var(--tm)', marginLeft: 6 }}><MapPin size={9} /> {m.material.bin_location}</span>
                  )}
                </span>
                <span style={{ color: 'var(--tm)', fontSize: 11 }}>{m.qty} {m.unit}</span>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                  background: m.status === 'received' ? 'var(--ok)' : isWaiting ? 'var(--warn)' : 'var(--bad)',
                  color: m.status === 'received' ? 'var(--ok)' : isWaiting ? 'var(--warn)' : 'var(--bad)',
                }}>
                  {m.status}
                </span>
              </div>
            );
          })}
          {/* C3: Delay alert for blocked tasks */}
          {isBlocked(statusSlug) && taskMaterials.some(m => ['requested', 'ordered', 'confirmed', 'in_transit'].includes(m.status)) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', marginTop: 6,
              background: 'var(--warn-tint)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--warn)' }}>
              <AlertTriangle size={13} />
              Waiting for materials — check PO status
            </div>
          )}
          {/* Material requests for this task */}
          {materialRequests.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: r.status === 'received' ? 'var(--ok)' : r.status === 'rejected' ? 'var(--bad)' : 'var(--warn)',
              }} />
              <span style={{ flex: 1, color: 'var(--t1)' }}>{r.material_name}</span>
              <span style={{ color: 'var(--tm)', fontSize: 11 }}>{r.qty} {r.unit}</span>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                background: r.status === 'requested' ? 'var(--info)' : r.status === 'approved' ? 'var(--ok)' : 'var(--bad)',
                color: r.status === 'requested' ? 'var(--accent)' : r.status === 'approved' ? 'var(--ok)' : 'var(--bad)',
              }}>
                {r.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* C5: Request Material button */}
      <button onClick={() => setShowRequestForm(v => !v)} style={{
        padding: 10, borderRadius: 12, fontSize: 13, fontWeight: 600, width: '100%',
        background: showRequestForm ? 'var(--navy-surface)' : 'var(--glass)',
        color: showRequestForm ? 'white' : 'var(--accent)',
        border: '1px solid var(--bdr)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer',
      }}>
        <Package size={14} /> Request Material
      </button>
      {showRequestForm && (
        <div style={{ ...glassBox }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <input type="text" placeholder="Material name *" value={requestForm.material_name}
              onChange={e => setRequestForm(p => ({ ...p, material_name: e.target.value }))}
              style={inp} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" placeholder="Qty" value={requestForm.qty} min={1}
                onChange={e => setRequestForm(p => ({ ...p, qty: Number(e.target.value) }))}
                style={{ ...inp, width: 80 }} />
              <select value={requestForm.urgency}
                onChange={e => setRequestForm(p => ({ ...p, urgency: e.target.value }))}
                style={{ ...inp, flex: 1 }}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <input type="text" placeholder="Notes (optional)" value={requestForm.notes}
              onChange={e => setRequestForm(p => ({ ...p, notes: e.target.value }))}
              style={inp} />
            <button onClick={async () => {
              if (!requestForm.material_name.trim()) return;
              try {
                await createMaterialRequest({
                  ...requestForm,
                  task_id: task.id,
                  project_name: projName,
                });
                setRequestForm({ material_name: '', qty: 1, urgency: 'normal', notes: '' });
                setShowRequestForm(false);
                fetchMaterialRequests({ task_id: task.id }).then(setMaterialRequests).catch((e) => logAppError({ type: 'api_error', message: `TaskNotesSection.refetch failed: ${e?.message || e}`, view: 'task.notes', metadata: { task_id: task.id, fn: 'fetchMaterialRequests', after: 'createMaterialRequest' } }));
              } catch (err) {
                console.warn('[TaskDetail] Request failed:', err.message);
              }
            }} style={{
              padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
              background: 'var(--navy-surface)', color: 'white', border: 'none', cursor: 'pointer',
            }}>
              Submit Request
            </button>
          </div>
        </div>
      )}

      {/* Voice Note (INST-05) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
        borderRadius: 14, background: 'var(--glass)', border: '1px solid var(--bdr)' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)', flex: 1 }}>Voice Note</span>
        <EntityMicButton contextType="task" contextId={task.id} />
      </div>
      {voiceNotes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {voiceNotes.map(vn => (
            <BreadcrumbCard key={vn.id} breadcrumb={vn} onRefresh={refreshVoice} />
          ))}
        </div>
      )}

      {/* Quick Note (INST-06) */}
      <div style={{ ...glassBox }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', marginBottom: 8,
          display: 'flex', alignItems: 'center', gap: 6 }}>
          <MessageSquare size={14} /> Quick Note
        </div>
        <textarea
          value={quickNote}
          onChange={e => setQuickNote(e.target.value)}
          placeholder={t('forms.addNote')}
          rows={2}
          style={{ ...inp, resize: 'none', marginBottom: 8 }}
        />
        <button
          onClick={async () => {
            if (!quickNote.trim() || savingNote) return;
            setSavingNote(true);
            try {
              await onUpdate({ notes: quickNote.trim() });
              setQuickNote('');
            } finally {
              setSavingNote(false);
            }
          }}
          disabled={!quickNote.trim() || savingNote}
          style={{
            padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
            background: quickNote.trim() && !savingNote ? 'var(--navy)' : 'var(--bdr)',
            color: quickNote.trim() && !savingNote ? 'var(--card)' : 'var(--tm)',
            border: 'none', cursor: quickNote.trim() && !savingNote ? 'pointer' : 'default',
            minHeight: 36,
          }}
        >
          {savingNote ? 'Saving...' : 'Save Note'}
        </button>
      </div>

      {/* Photo Upload (INST-04) */}
      <div style={{ ...glassBox }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', marginBottom: 8,
          display: 'flex', alignItems: 'center', gap: 6 }}>
          <Camera size={14} /> Photo
        </div>
        {photoError && (
          <div style={{ color: 'var(--bad)', fontSize: 12, marginBottom: 6 }}>{photoError}</div>
        )}
        <label style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 10, minHeight: 44,
          background: photoUploading ? 'var(--bdr)' : 'var(--glass)',
          border: '1px dashed var(--bdr)', cursor: photoUploading ? 'default' : 'pointer',
          fontSize: 13, fontWeight: 600, color: photoUploading ? 'var(--tm)' : 'var(--accent)',
        }}>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            disabled={photoUploading}
            onClick={() => markPickerOpen()}
            onChange={async (e) => {
              clearPickerOpen(); // TICKET-1439: returned without an OS reload
              const file = e.target.files?.[0];
              if (!file) return;
              setPhotoError(null);
              setPhotoUploading(true);
              try {
                // eslint-disable-next-line no-restricted-imports -- storage.from('task-photos') for photo upload
                const { supabase } = await import('../../lib/supabase.js');

                const ext = file.name.split('.').pop() || 'jpg';
                const path = `${task.id}/${Date.now()}.${ext}`;
                // TICKET-658: ArrayBuffer, not File — storage-js wraps a
                // Blob/File body in FormData with an empty field name, which
                // WebKit intermittently mis-serializes on larger payloads
                // ("No content provided" server-side).
                const { error: storageErr } = await supabase.storage
                  .from('task-photos')
                  .upload(path, await file.arrayBuffer(), { upsert: false });
                if (storageErr) throw new Error(storageErr.message);

                const { data: { publicUrl } } = supabase.storage
                  .from('task-photos')
                  .getPublicUrl(path);

                const payload = {
                  action: 'files.create',
                  task_id: task.id,
                  project_id: projectId || undefined,
                  title: file.name || `Photo ${new Date().toLocaleDateString('en-GB')}`,
                  file_type: 'photo',
                  storage_provider: 'url',
                  external_url: publicUrl,
                };
                const fileRes = await apiFetch('/api/ops', {
                  method: 'POST',
                  body: JSON.stringify(payload),
                });
                if (fileRes?.success === false) {
                  throw new Error(fileRes.error || 'Failed to save photo record');
                }
              } catch (err) {
                console.error('[TaskDetailPanel] photo upload error:', err);
                setPhotoError(err.message || 'Failed to upload photo');
              } finally {
                setPhotoUploading(false);
                e.target.value = '';
              }
            }}
          />
          {photoUploading ? 'Uploading...' : 'Capture or Select Photo'}
        </label>
      </div>
    </>
  );
}
